<script setup lang="ts">
import { useRouter } from 'vue-router'
import { API } from '@/modules/api'
import { useSessionStore } from '@/stores/session'
import { Util } from '@/modules/util'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'
import { IframeContext } from '@/modules/iframeContext'

const sessionStore = useSessionStore()

const i18n = useI18n()

const router = useRouter()

const toast = useToast()

const props = defineProps({
  name: {
    type: String,
    required: false
  },
  img: {
    type: String,
    required: false
  },
  imgHover: {
    type: String,
    required: false
  },
  icon: {
    type: String,
    required: false
  },
  route: {
    type: String,
    required: false
  },
  alt: {
    type: String,
    required: false
  },
  appId: {
    type: String,
    required: false
  }
})

const clicked = () => {
  if (props.appId) {
    console.debug('Redirecting to app ID ' + props.appId)
    const payload = {
      district_id: null,
      family_account_id: null,
      extra_data: { app_to: props.appId, app_from: 'user_services_dashboard' }
    }

    // Check if we're in an iframe (PowerSchool context)
    const isInIframe = IframeContext.isInIframe()

    API.Post.generateSsoUrl(Util.Obj.keysToSnake(payload))
      .then(results => {
        if (results?.success) {
          // Default to true (logout enabled) if env var is not set
          const shouldLogout = import.meta.env.VITE_SSO_LOGOUT !== 'false'

          if (isInIframe) {
            // In iframe (PowerSchool): Open external apps in new tab without logging out
            console.debug('Opening external app in new tab (iframe context)')
            window.open(results?.ssoUrl + '/sso_router?token=' + results?.token, '_blank')
            setTimeout(() => {
              window.location.reload()
            }, 3000)
          } else if (shouldLogout) {
            // Not in iframe (Lobby) and logout enabled: Open external apps in same tab with logout
            console.debug('Opening external app in same tab with logout (lobby context)')
            API.Post.logout()
              .then(() => {
                sessionStore.$reset()
                window.open(results?.ssoUrl + '/sso_router?token=' + results?.token, '_self')
              })
              .catch((error: any) => {
                toast.add({
                  severity: 'error',
                  summary: i18n.t('c.nav.failedToLogout'),
                  detail: error,
                  life: 5000
                })
              })
          } else {
            // Not in iframe and logout disabled: Open in new tab without logging out
            console.debug('Opening external app in new tab (logout disabled)')
            window.open(results?.ssoUrl + '/sso_router?token=' + results?.token, '_self')
          }
        }
        if (!results?.success) {
          toast.add({
            severity: 'error',
            summary: i18n.t('c.error'),
            detail: results?.failureReason,
            life: 5000
          })
        }
      })
      .catch(err => {
        console.error(err)
        toast.add({
          severity: 'error',
          summary: i18n.t('c.error'),
          detail: i18n.t('c.nav.failedToGenerateSsoUrl'),
          life: 3000
        })
      })
  }
  if (props.route !== undefined) {
    console.debug('Redirecting to route ' + props.route)
    router.push(props.route)
  }
}
</script>

<template>
  <Button
    @click="clicked"
    :class="'NavButton' + (route !== '/dashboard' ? ' NavHover' : '')"
    :variant="route ? 'link' : undefined"
    severity="secondary"
  >
    <div v-if="icon" class="NavButton_iconWrapper">
      <i :class="icon" class="NavButton_icon" />
    </div>
    <img v-if="img" class="NavButton_img" :src="img" :alt="alt" />
    <span v-if="name" class="NavButton_name">{{ name }}</span>
    <slot></slot>
  </Button>
</template>

<style scoped>
.NavButton {
  display: flex;
  align-items: center;
  font-size: var(--font-size-base);
  padding: 7px;
  /* width: 100%; */
  justify-content: flex-start;
}

.NavHover:hover {
  background-color: #d7d7d7;
}

.NavButton_img {
  width: var(--icon-size-sm);
  height: var(--icon-size-sm);
  padding-bottom: 2px; /* Needed for centering */
}

.NavButton_name {
  color: var(--p-surface-800);
}

.NavButton_iconWrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--icon-size-sm);
  height: var(--icon-size-sm);
  border-radius: 50%;
  border: 2px solid var(--p-green-500);
}

.NavButton_icon {
  display: flex;
  align-items: center;
  color: var(--p-green-500);
  font-size: 17px;
}

@media (min-width: 48rem) {
}
</style>
